import { Box, Grid, Modal, Stack, Tab, Tabs, useTheme } from '@mui/material';
import Drawer from 'components/Drawer/Drawer';
import DeleteVisitor from 'components/Modal/variants/visitor/DeleteVisitor';
import dayjs from 'dayjs';
import { UserIdentityProvider } from 'graphql/generated/graphqlTypes';
import useVisitorDetails from 'hooks/allVisitors/pages/userDetails/useVisitorDetails';
import useTranslation, { Translation } from 'hooks/useTranslation/useTranslation';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import Loader from 'v2/components/Loader/Loader';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import NotFound from 'v2/pages/GeneralErrors/NotFound';
import SomethingWentWrong from 'v2/pages/GeneralErrors/SomethingWentWrong';
import AccessEventsTab from './components/AccessEventsTab';
import AccessOverviewTab from './components/AccessOverviewTab';
import AccessSection from './components/AccessSection';
import GroupsSection from './components/GroupsSection';
import Header from './components/Header';
import UserInfoSection from './components/UserInfoSection';

export const createTabsHeader = (translation: Translation): string[] => [
  translation.access_overview,
  translation.events
];

const UserDetails: React.FC = () => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const tabsHeader = createTabsHeader(translation);
  const { userId } = useParams();
  const [currentTabIndex, setCurrentTabIndex] = React.useState(0);
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const {
    visitorDetails,
    loadingVisitorDetails,
    isVisitorNotFound,
    hasVisitorFetchError,
    retryFetchingVisitorDetails,
    deleteVisitor,
    deleteVisitorLoading
  } = useVisitorDetails({
    translation,
    handleFetchError,
    visitorId: userId ?? ''
  });
  const navigate = useNavigate();
  const [isDeleteVisitorModalOpen, setIsDeleteVisitorModalOpen] = useState(false);

  const handleOpenDeleteVisitorModal = () => {
    setIsDeleteVisitorModalOpen(true);
  };

  const handleCloseDeleteVisitorModal = () => {
    setIsDeleteVisitorModalOpen(false);
  };

  const handleDeleteVisitor = async () => {
    if (isDeleteVisitorModalOpen && !deleteVisitorLoading && !loadingVisitorDetails && visitorDetails) {
      try {
        await deleteVisitor(visitorDetails.id);
      } finally {
        setIsDeleteVisitorModalOpen(false);
        navigate('../');
      }
    }
  };

  const handleChangeTab = (_event: React.SyntheticEvent, newIndex: number) => {
    setCurrentTabIndex(newIndex);
  };

  if (loadingVisitorDetails) {
    return (
      <Box justifyContent="center" alignContent="center" sx={{ width: '100%', height: '60vh' }}>
        <Loader />
      </Box>
    );
  }

  if (isVisitorNotFound) {
    return <NotFound />;
  }

  if (hasVisitorFetchError || !visitorDetails) {
    return <SomethingWentWrong onRetry={retryFetchingVisitorDetails} />;
  }

  return (
    <>
      <Stack spacing={4}>
        <BreadcrumbMeta
          breadcrumb={{
            title: visitorDetails.name,
            crumbs: [
              {
                text: translation.users,
                path: '/users'
              },
              {
                text: translation.all_users,
                path: '/users/overview'
              }
            ]
          }}
        />
        <Header
          visitorEmail={visitorDetails.email}
          visitorName={visitorDetails.name}
          isDeleteVisitorButtonVisible={Boolean(
            visitorDetails?.identityProvider === UserIdentityProvider.Internal ||
              visitorDetails?.identityProvider === UserIdentityProvider.Cognito
          )}
          handleOpenDeleteVisitorModal={handleOpenDeleteVisitorModal}
        />
        <Grid container spacing={4} direction={{ xs: 'column', lg: 'row' }} wrap="nowrap">
          <Grid container direction="column" spacing={4} size={{ xs: 12, lg: 4 }} wrap="nowrap">
            <Grid size={12}>
              <UserInfoSection
                visitorId={visitorDetails.id}
                visitorIdentityProvider={visitorDetails.identityProvider}
                visitorExpirationDate={visitorDetails.expirationDate ? dayjs(visitorDetails.expirationDate) : undefined}
                visitorVisitorGroupMembership={visitorDetails.visitorGroupMembership}
              />
            </Grid>
            <Grid container spacing={4}>
              <Grid size={12}>
                <AccessSection visitorId={visitorDetails.id} />
              </Grid>
              <Grid size={12}>
                <GroupsSection visitor={visitorDetails} />
              </Grid>
            </Grid>
          </Grid>
          <Grid container direction="column" size={{ xs: 12, lg: 8 }} wrap="nowrap">
            <Box sx={{ borderBottom: 1, borderColor: theme.palette.divider }}>
              <Tabs value={currentTabIndex} onChange={handleChangeTab}>
                {tabsHeader.map((header, index) => (
                  <Tab key={header} id={`tab-${index}`} label={header} />
                ))}
              </Tabs>
            </Box>
            {currentTabIndex === 0 && (
              <Grid container role="tabpanel" hidden={currentTabIndex !== 0} id={`tabpanel-${0}`}>
                <Box sx={{ pt: 3 }} style={{ flex: 1 }}>
                  <AccessOverviewTab visitorId={visitorDetails.id} />
                </Box>
              </Grid>
            )}
            {currentTabIndex === 1 && (
              <Grid container role="tabpanel" hidden={currentTabIndex !== 1} id={`tabpanel-${1}`}>
                <Box sx={{ pt: 3 }} style={{ flex: 1 }}>
                  <AccessEventsTab visitorId={visitorDetails.id} />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Stack>

      <Drawer />
      <Modal
        open={isDeleteVisitorModalOpen}
        onClose={handleCloseDeleteVisitorModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <DeleteVisitor handleDeleteVisitor={handleDeleteVisitor} handleClose={handleCloseDeleteVisitorModal} />
      </Modal>
    </>
  );
};
export default UserDetails;
